import React, { useEffect, useState } from 'react';
import { AuthState } from '../../redux/features/auth/authSlice';
import api from '../../redux/features/auth/axiosAuthHelper';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';

export interface OrganisationData {
  id: string;
  name: string;
}

export interface UserData {
  id: number;
  name: string;
}

export const drugCodes = [
  { value: "ALC", label: "ALC" },
  { value: "AMP", label: "AMP" },
  { value: "BAR", label: "BAR" },
  { value: "BUP", label: "BUP" },
  { value: "BZO", label: "BZO" },
  { value: "COC", label: "COC" },
  { value: "COT", label: "COT" },
  { value: "FYL", label: "FYL" },
  { value: "K2", label: "K2" },
  { value: "MET", label: "MET" },
  { value: "MTD", label: "MTD" },
  { value: "OPI", label: "OPI" },
  { value: "OXY", label: "OXY" },
  { value: "PCP", label: "PCP" },
  { value: "THC", label: "THC" },
  { value: "TML", label: "TML" },
];


/**
 * Custom hook to fetch and manage clinic data based on the user type.
 * @param authData - The authData object containing authentication-related information.
 * @returns An array containing clinics.
 */
const useOrganisations = () => {
  // State to hold the fetched clinic data.
  const [organisations, setOrganisations] = useState<OrganisationData[]>([]);
  const authData = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // Skip fetching if the user is not authenticated.
    if (!authData.isAuthenticated) {
      return;
    }

    // Asynchronous function for fetching clinic data.
    const fetchData = async () => {
      try {
        let organisationData: OrganisationData[] = [];
        if (authData.isSuperUser || authData.isPortalUser) {
          // Fetch Organisations using the 'api/organisations/' endpoint for administrators and support users.
          const response = await api.get('organisations/', {
            headers: {
              Authorization: `Bearer ${authData.access}`,
            },
          });

          organisationData = (response.data.results as OrganisationData[]).map(
            ({ id, name }) => ({
              id,
              name,
            }),
          );
          // Update the component state with the fetched organisation data.
          setOrganisations(organisationData);
        }
        console.log('-----Organisations------');
        console.log(organisationData);
      } catch (error) {
        // Log an error message if there is an issue fetching organisation data.
        console.error('Error fetching organisation data:', error);
      }
    };

    // Call the fetchData function to initiate the effect.
    fetchData();
  }, [authData.isAuthenticated, authData.isSuperUser, authData.isPortalUser]);

  return [organisations] as const;
};

const useRareOrganisations = (): OrganisationData[] => {
  // State to hold the fetched clinic data.
  const [organisations, setOrganisations] = useState<OrganisationData[]>([]);
  const authData = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // Skip fetching if the user is not authenticated.
    if (!authData.isAuthenticated) {
      return;
    }

    // Asynchronous function for fetching clinic data.
    const fetchData = async () => {
      try {
        let organisationData: OrganisationData[] = [];
        let downloadPageSize = 250;  // Set the batch size to 250
        let downloadOffset = 0;
        let total = 0;
        if (authData.isSuperUser || authData.isPortalUser) {
          // Fetch Organisations using the 'api/organisations/' endpoint for administrators and support users.
          const initialResponse = await api.get(`organisations/?limit=${downloadPageSize}&offset=${downloadOffset}`, {
            headers: {
              Authorization: `Bearer ${authData.access}`,
            },
          });

          organisationData = (initialResponse.data.results as OrganisationData[]).map(
            ({ id, name }) => ({
              id,
              name,
            }),
          );
          // Update the component state with the fetched organisation data.
          total = initialResponse.data.count;
          setOrganisations([...organisationData].sort((a, b) => Number(b.id) - Number(a.id)));


          // Now fetch the remaining in the background
          while (organisationData.length < total) {
            downloadOffset += downloadPageSize;
            const url = `organisations/?limit=${downloadPageSize}&offset=${downloadOffset}`

            const response = await api.get(url, {
              headers: {
                'Authorization': `Bearer ${authData.access}`
              }
            });
            const nextPageorganisationData = (response.data.results as OrganisationData[]).map(
              ({ id, name }) => ({
                id,
                name,
              }),
            );

            organisationData.push(...nextPageorganisationData);
            console.log(`Downloaded ${organisationData.length} of ${total} organisations`);
            setOrganisations([...organisationData].sort((a, b) => Number(b.id) - Number(a.id)));
          }

        }
        console.log('-----Organisations------');
        console.log(organisationData);
      } catch (error) {
        // Log an error message if there is an issue fetching organisation data.
        console.error('Error fetching organisation data:', error);
      }
    };

    // Call the fetchData function to initiate the effect.
    fetchData();
  }, [authData]);

  return organisations;
};


const useRareUsers = (): UserData[] => {
  const [users, setUsers] = useState<UserData[]>([]);
  const authData = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (!authData.isAuthenticated) {
      return;
    }

    const fetchData = async () => {
      try {
        let userData: UserData[] = [];
        const downloadPageSize = 250;
        let downloadOffset = 0;
        let total = 0;

        if (authData.isSuperUser || authData.isPortalUser) {
          const initialResponse = await api.get(
            `users/?limit=${downloadPageSize}&offset=${downloadOffset}`,
            { headers: { Authorization: `Bearer ${authData.access}` } }
          );

          userData = (initialResponse.data.results as any[]).map((user) => ({
            id: user.id,
            name: `${user.first_name} ${user.last_name}`.trim(),
          }));
          total = initialResponse.data.count;
          setUsers([...userData]);

          while (userData.length < total) {
            downloadOffset += downloadPageSize;
            const response = await api.get(
              `users/?limit=${downloadPageSize}&offset=${downloadOffset}`,
              { headers: { Authorization: `Bearer ${authData.access}` } }
            );
            const nextPageUserData = (response.data.results as any[]).map((user) => ({
              id: user.id,
              name: `${user.first_name} ${user.last_name}`.trim(),
            }));
            userData.push(...nextPageUserData);
            setUsers([...userData]);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchData();
  }, [authData]);

  return users;
};

export {
  useOrganisations,
  useRareOrganisations,
  useRareUsers
};
